import { AxiosResponse } from 'axios'
import { http } from '../realisations/Http.realisation'
import {
    CarChangeStatusPayload,
    CourierCarCreatePayloadT,
    CourierCarPayloadT,
    CourierPayloadT,
    CouriersCloseDayResponseT,
    CouriersCreateCourierResponseT,
    CouriersDownloadCourierPayloadT,
    CouriersDownloadCourierResponseT,
    CouriersGetCarResponseT,
    CouriersGetCarsResponseT,
    CouriersGetCarsStatusesT,
    CouriersGetCourierResponseT,
    CouriersGetListResponseT,
    CouriersGetVehicleTypesResponseT,
    CouriersUpdateCourierResponseT,
    VehicleTypePayload,
    CouriersGetParamsT,
    SchedulePayload,
    CouriersCreateCarResponseT,
    CouriersGetTypesResponseT,
} from '../types/couriers.types'
import { GetCallsParamsT, GetCallsResponseT } from '../types/calls.types'

export class CouriersRepository {
    getList(params: any): Promise<AxiosResponse<CouriersGetListResponseT>> {
        return http.get('/dispatcher/api/couriers', { params })
    }

    getCourierTypes(): Promise<AxiosResponse<CouriersGetTypesResponseT>> {
        return http.get('/dispatcher/api/courier-types')
    }

    getCalls(params: GetCallsParamsT): Promise<AxiosResponse<GetCallsResponseT>> {
        return http.get('/dispatcher/api/courier-app/courier-calls', { params })
    }

    createCourier(
        courier_payload: FormData
    ): Promise<AxiosResponse<CouriersCreateCourierResponseT>> {
        return http.postFormData('/dispatcher/api/couriers', courier_payload)
    }

    createCategory(category_payload: VehicleTypePayload): Promise<AxiosResponse<void>> {
        return http.post('/cabinet/api/dispatcher-admin/vehicle-types', category_payload)
    }

    createSchedule(schedule_payload: SchedulePayload): Promise<AxiosResponse<void>> {
        return http.post('/dispatcher/api/courier-schedule', schedule_payload)
    }

    createJira(): Promise<AxiosResponse<void>> {
        return http.post('/dispatcher/api/couriers/store-via-jira', {})
    }

    updateCourier(
        courier_id: number,
        courier_payload: CourierPayloadT
    ): Promise<AxiosResponse<CouriersUpdateCourierResponseT>> {
        return http.put(`/dispatcher/api/couriers/${courier_id}`, courier_payload)
    }

    updateCategory(
        category_id: number,
        category_payload: VehicleTypePayload
    ): Promise<AxiosResponse<CouriersUpdateCourierResponseT>> {
        return http.put(
            `/cabinet/api/dispatcher-admin/vehicle-types/${category_id}`,
            category_payload
        )
    }

    deleteCourier(courier_id: number): Promise<AxiosResponse<void>> {
        return http.delete(`/cabinet/api/courier-app/courier/${courier_id}`)
    }

    deleteCategory(category_id: number): Promise<AxiosResponse<void>> {
        return http.delete(`/cabinet/api/dispatcher-admin/vehicle-types/${category_id}`)
    }

    getVehicleTypes(page: number): Promise<AxiosResponse<CouriersGetVehicleTypesResponseT>> {
        const params = {
            page,
        }

        return http.get('/cabinet/api/dispatcher-admin/vehicle-types', { params })
    }

    exportExcel(
        params: CouriersDownloadCourierPayloadT
    ): Promise<AxiosResponse<CouriersDownloadCourierResponseT>> {
        return http.get('/cabinet/api/courier-app/courier/export', {
            responseType: 'blob',
            params: { ...params },
        })
    }

    setCheckupStatus(courier_id: number): Promise<AxiosResponse<void>> {
        return http.put(`/dispatcher/api/courier/${courier_id}/status-in-checkup`, {})
    }

    setActiveStatus(courier_id: number): Promise<AxiosResponse<void>> {
        return http.put(`/dispatcher/api/courier/${courier_id}/status-active`, {})
    }

    setArchiveStatus(courier_id: number): Promise<AxiosResponse<void>> {
        return http.put(`/dispatcher/api/courier/${courier_id}/status-archive`, {})
    }

    getCourier(courier_id: number): Promise<AxiosResponse<CouriersGetCourierResponseT>> {
        return http.get(`/dispatcher/api/couriers/${courier_id}`)
    }

    uploadDocument(courier_id: number, form_data: FormData): Promise<AxiosResponse<void>> {
        return http.postFormData(
            `/cabinet/api/courier-app/courier/${courier_id}/upload-document`,
            form_data
        )
    }

    uploadCarDocument(car_id: number, form_data: FormData): Promise<AxiosResponse<void>> {
        return http.postFormData(
            `/cabinet/api/courier-app/car/${car_id}/upload-document`,
            form_data
        )
    }

    getCars(params: CouriersGetParamsT): Promise<AxiosResponse<CouriersGetCarsResponseT>> {
        return http.get('/cabinet/api/courier-app/car', { params })
    }

    getCar(car_id: number): Promise<AxiosResponse<CouriersGetCarResponseT>> {
        return http.get(`/cabinet/api/courier-app/car/${car_id}`)
    }

    createCar(
        car_payload: CourierCarCreatePayloadT
    ): Promise<AxiosResponse<CouriersCreateCarResponseT>> {
        return http.postFormData('/cabinet/api/courier-app/car', car_payload)
    }

    updateCar(car_id: number, car_payload: CourierCarPayloadT): Promise<AxiosResponse<void>> {
        return http.put(`/cabinet/api/courier-app/car/${car_id}`, car_payload)
    }

    deleteCar(car_id: number): Promise<AxiosResponse<void>> {
        return http.delete(`/cabinet/api/courier-app/car/${car_id}`)
    }

    getCarsStatuses(params: any): Promise<AxiosResponse<CouriersGetCarsStatusesT>> {
        return http.get('/cabinet/api/dictionaries/car-statuses', { params })
    }

    changeCarStatus(payload: CarChangeStatusPayload): Promise<AxiosResponse<void>> {
        return http.put('/cabinet/api/courier-app/car/change-status', payload)
    }

    closeDay(
        courier_id: number,
        date: string | null
    ): Promise<AxiosResponse<CouriersCloseDayResponseT>> {
        return http.post(`/cabinet/api/courier-app/courier/${courier_id}/close-day`, {
            date,
        })
    }

    saveShortcoming(payload: FormData): Promise<AxiosResponse<void>> {
        return http.post('/dispatcher/api/courier-app/order-take/save-shortcoming-files', payload)
    }

    savePaymentTakes(payload: FormData): Promise<AxiosResponse<void>> {
        return http.post('/dispatcher/api/courier-app/order-take/courier-payment', payload)
    }

    savePaymentDeliveries(payload: FormData): Promise<AxiosResponse<void>> {
        return http.post('/dispatcher/api/courier-app/delivery/courier-payment', payload)
    }

    rejectCourier(courier_id: number): Promise<AxiosResponse<void>> {
        return http.put(`/dispatcher/api/courier/${courier_id}/status-reject`, {})
    }
}
